import { Card, CardContent, CardHeader, CardTitle, CardFooter } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Book, Calendar, DollarSign, Info, CheckCircle, Clock, User, Car } from "lucide-react";
import { Course } from "@/types/courseTypes";
import { useNavigate } from "react-router-dom";

// Extend CourseCardProps to receive an optional enrollment prop
interface CourseCardProps {
  course: Course;
  isEnrolled: boolean;
  onViewCourse: () => void;
  enrollment?: {
    startDate?: string;
    preferredTime?: string;
    instructorName?: string;
    additionalNotes?: string;
    drivingExperience?: string;
    status?: string;
  }; // Only for practical enrollments
}

const formatDate = (dateStr?: string) => {
  if (!dateStr) return "N/A";
  try {
    return new Date(dateStr).toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric"
    });
  } catch {
    return "Invalid date";
  }
};

const CourseCard = ({ course, isEnrolled, onViewCourse, enrollment }: CourseCardProps) => {
  const isPractical = course.type === "practical";
  const navigate = useNavigate();

  const handleBookNow = () => {
    if (isPractical) {
      navigate(`/student-dashboard/practical-course/${course.id}/enroll`);
    } else {
      onViewCourse();
    }
  };

  return (
    <Card className="overflow-hidden flex flex-col">
      <div className="h-40 bg-gray-100 relative">
        {course.thumbnail && (
          <img
            src={course.thumbnail}
            alt={course.title}
            className="w-full h-full object-cover"
          />
        )}
        {isEnrolled && (
          <div className="absolute top-2 right-2">
            <Badge className="bg-green-500">
              {course.type === "theory" ? "Enrolled" : "Booked"}
            </Badge>
          </div>
        )}
      </div>
      <CardHeader className="pb-2">
        <CardTitle>{course.title}</CardTitle>
        <div className="flex items-center mt-1">
          {course.type === "theory" ? (
            <Book className="h-4 w-4 text-blue-500 mr-1" />
          ) : (
            <Calendar className="h-4 w-4 text-blue-500 mr-1" />
          )}
          <span className="text-sm text-gray-600">
            {course.type === "theory" ? "Theory Course" : course.subtype === "individual" ? "Individual Lesson" : "Practical Course"}
          </span>
        </div>
      </CardHeader>
      <CardContent className="flex-1">
        {isPractical && isEnrolled && enrollment ? (
          <div className="bg-blue-50 p-4 rounded-md my-2">
            <div className="mb-1 flex items-center gap-2">
              <Calendar className="h-4 w-4 text-blue-400" />
              <span className="text-sm">
                <span className="font-semibold">Date:</span> {formatDate(enrollment.startDate)}
              </span>
            </div>
            <div className="mb-1 flex items-center gap-2">
              <Clock className="h-4 w-4 text-blue-400" />
              <span className="text-sm">
                <span className="font-semibold">Time:</span> {enrollment.preferredTime || "N/A"}
              </span>
            </div>
            {enrollment.instructorName && (
              <div className="mb-1 flex items-center gap-2">
                <User className="h-4 w-4 text-blue-400" />
                <span className="text-sm">
                  <span className="font-semibold">Instructor:</span> {enrollment.instructorName}
                </span>
              </div>
            )}
            {enrollment.drivingExperience && (
              <div className="mb-1 text-sm">
                <span className="font-semibold">Experience:</span> {enrollment.drivingExperience}
              </div>
            )}
            {enrollment.additionalNotes && (
              <div className="text-sm">
                <span className="font-semibold">Notes:</span> {enrollment.additionalNotes}
              </div>
            )}
          </div>
        ) : (
          <>
            <p className="text-sm text-gray-600 line-clamp-3 mb-2">
              {course.description}
            </p>
            <div className="space-y-2">
              {course.type === "practical" && (
                <>
                  <div className="flex items-center">
                    <Clock className="h-4 w-4 text-gray-400 mr-1" />
                    <span className="text-sm text-gray-600">
                      {course.subtype === "individual" 
                        ? "Flexible Hours" 
                        : `${course.totalHours || course.duration} hours`}
                    </span>
                  </div>
                  <div className="flex items-center">
                    <span className="text-sm text-gray-600">
                      {course.subtype === "individual"
                        ? `${'\u00A3'}${course.pricePerHour?.toFixed(2)}/hour`
                        : `${'\u00A3'}${course.price?.toFixed(2)}`}
                    </span>
                  </div>
                  {course.subtype === "practical" && (
                    <div className="flex items-center">
                      <Car className="h-4 w-4 text-gray-400 mr-1" />
                      <span className="text-sm text-gray-600">Car Hire Included</span>
                    </div>
                  )}
                </>
              )}
            </div>
          </>
        )}
      </CardContent>
      <CardFooter>
        {isEnrolled ? (
          <Button
            variant="outline"
            className="w-full"
            onClick={onViewCourse}
          >
            View Details
          </Button>
        ) : (
          <Button
            className="w-full bg-gradient-to-r from-drivewise-500 to-blue-500 hover:from-drivewise-600 hover:to-blue-600"
            onClick={handleBookNow}
          >
            {course.type === "theory" ? "Enroll Now" : "Book Now"}
          </Button>
        )}
      </CardFooter>
    </Card>
  );
};

export default CourseCard;
